import { captureGPSFailure } from '@services/monitoring';
// src/services/geolocation/index.js
// All location services: GPS, distance math, reverse geocoding,
// parish detection, and flood zone proximity.

// ── Haversine distance ────────────────────────────────────────────
export function haversineKm(lat1, lon1, lat2, lon2) {
  const R    = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a    = Math.sin(dLat / 2) ** 2
             + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180)
             * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function haversineMiles(lat1, lon1, lat2, lon2) {
  return haversineKm(lat1, lon1, lat2, lon2) * 0.621371;
}

// ── Current position ──────────────────────────────────────────────
export function getCurrentPosition(opts = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported on this device'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat:      pos.coords.latitude,
        lon:      pos.coords.longitude,
        accuracy: pos.coords.accuracy,   // metres
        heading:  pos.coords.heading,    // degrees from north, null if unavailable
        speed:    pos.coords.speed,      // m/s, null if unavailable
      }),
      (err) => {
        const msgs = {
          1: 'Location access denied. Enable in Settings > Privacy > Location Services.',
          2: 'GPS signal unavailable. Move to an open area and try again.',
          3: 'GPS timed out. Enter your address manually.',
        };
        const message = msgs[err.code] ?? 'GPS error. Enter your address manually.';
        captureGPSFailure(err.code, message);
        reject(new Error(message));
      },
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 0, ...opts }
    );
  });
}

// Watch position (continuous — call stopWatching to clean up)
export function watchPosition(onUpdate, onError, opts = {}) {
  if (!navigator.geolocation) {
    onError?.(new Error('Geolocation not supported'));
    return () => {};
  }
  const id = navigator.geolocation.watchPosition(
    (pos) => onUpdate({
      lat:      pos.coords.latitude,
      lon:      pos.coords.longitude,
      accuracy: pos.coords.accuracy,
    }),
    (err) => onError?.(err),
    { enableHighAccuracy: true, maximumAge: 5_000, ...opts }
  );
  return () => navigator.geolocation.clearWatch(id);
}

// ── Parish centers (for topic subscription geofencing) ────────────
export const PARISH_CENTERS = {
  'Orleans':     { lat: 29.9511, lon: -90.0715 },
  'Jefferson':   { lat: 29.9011, lon: -90.1734 },
  'St. Bernard': { lat: 29.8514, lon: -89.8289 },
  'Plaquemines': { lat: 29.5007, lon: -89.8239 },
  'St. Tammany': { lat: 30.3851, lon: -89.9287 },
  'St. Charles': { lat: 29.9460, lon: -90.3632 },
  'St. John':    { lat: 30.0118, lon: -90.4635 },
};

// Returns parishes within radiusKm of the given point
export function nearbyParishes(lat, lon, radiusKm = 25) {
  return Object.entries(PARISH_CENTERS)
    .filter(([, center]) => haversineKm(lat, lon, center.lat, center.lon) <= radiusKm)
    .map(([parish]) => parish);
}

// Best-guess parish from coordinates (nearest centroid)
export function inferParish(lat, lon) {
  let best = null;
  let bestDist = Infinity;
  for (const [parish, center] of Object.entries(PARISH_CENTERS)) {
    const d = haversineKm(lat, lon, center.lat, center.lon);
    if (d < bestDist) { bestDist = d; best = parish; }
  }
  return best;
}

// ── App zone from coordinates ─────────────────────────────────────
// Approximate bounding boxes for the app zones. Good enough for
// default zone selection — not a substitute for FEMA flood zone data.
const ZONE_BOXES = [
  { zone: 'N.O. East',      latMin: 29.97, latMax: 30.07, lonMin: -90.05, lonMax: -89.85 },
  { zone: 'Lakeview',       latMin: 29.98, latMax: 30.01, lonMin: -90.14, lonMax: -90.07 },
  { zone: 'Gentilly',       latMin: 29.98, latMax: 30.02, lonMin: -90.08, lonMax: -90.02 },
  { zone: 'Lower 9th Ward', latMin: 29.96, latMax: 30.00, lonMin: -90.02, lonMax: -89.96 },
  { zone: 'Carrollton',     latMin: 29.94, latMax: 29.98, lonMin: -90.17, lonMax: -90.08 },
  { zone: 'Uptown',         latMin: 29.91, latMax: 29.95, lonMin: -90.16, lonMax: -90.09 },
  { zone: 'Mid-City',       latMin: 29.95, latMax: 29.98, lonMin: -90.12, lonMax: -90.07 },
  { zone: 'French Quarter', latMin: 29.94, latMax: 29.97, lonMin: -90.08, lonMax: -90.05 },
  { zone: 'Algiers',        latMin: 29.88, latMax: 29.94, lonMin: -90.10, lonMax: -89.98 },
  { zone: 'Northshore / Slidell', latMin: 30.20, latMax: 30.55, lonMin: -90.20, lonMax: -89.60 },
];

export function inferZone(lat, lon) {
  const match = ZONE_BOXES.find(
    (b) => lat >= b.latMin && lat <= b.latMax && lon >= b.lonMin && lon <= b.lonMax
  );
  return match?.zone ?? null;
}

// ── Coordinate formatting ─────────────────────────────────────────
export function formatCoords(lat, lon, decimals = 5) {
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(decimals)}°${ns}, ${Math.abs(lon).toFixed(decimals)}°${ew}`;
}

// ── Google Maps deep link ─────────────────────────────────────────
export function mapsDirectionsUrl(lat, lon, label = '') {
  const dest = label
    ? encodeURIComponent(label)
    : `${lat},${lon}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
}

// ── Geospatial sub-modules ────────────────────────────────────────
// Import directly from sub-files for tree-shaking:
//   import { encode, geohashQueryRanges } from '@services/geolocation/geohash'
//   import { SpatialIndex }               from '@services/geolocation/spatialIndex'
//   import { nearbyReports }              from '@services/geolocation/geoQueries'
//
// Re-export convenience functions for common use:
export { geohashForReport, geohashForZone, geohashForParish, geohashQueryRanges } from './geohash.js';
export { geoQuery, nearbyReports, nearbySOSReports, nearbyActiveIncidents, nearbyShelters, nearbyOpenGas } from './geoQueries.js';
export { SpatialIndex, getShelterIndex, getHospitalIndex, getIncidentIndex, getGasIndex, rebuildIndex } from './spatialIndex.js';
